import React, { useState, useEffect } from 'react'
import { Form, Input, Button, Card, message, Typography } from 'antd'
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@store'
import { login as loginThunk } from '@store/slices/authSlice'

const { Title, Text } = Typography

const CN_MOBILE = /^1[3-9]\d{9}$/

interface LoginForm {
  username: string
  password: string
}

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [icpNumber, setIcpNumber] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    fetch('/api/auth/config')
      .then((r) => r.json())
      .then((j) => {
        if (j?.success && j?.data?.icpNumber) {
          setIcpNumber(j.data.icpNumber)
        }
        if (j?.success && j?.data?.logoUrl) {
          setLogoUrl(j.data.logoUrl)
        }
      })
      .catch(() => {})
  }, [])

  const handleLogin = async (values: LoginForm) => {
    setLoading(true)
    try {
      await dispatch(loginThunk(values)).unwrap()
      message.success('登录成功')
      navigate('/dashboard', { replace: true })
    } catch (error: any) {
      console.error('LoginPage: 登录失败:', error)
      const msg = typeof error === 'string' ? error : error?.message || '登录失败'
      message.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
        }}
        styles={{ body: { padding: '40px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          {logoUrl ? (
            <img src={logoUrl} alt="西舟菜园" style={{ width: 120, height: 120, borderRadius: 12, objectFit: 'contain', margin: '0 auto 16px', display: 'block' }} />
          ) : (
            <div style={{
              width: '64px', height: '64px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <LoginOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
          )}
          <Title level={2} style={{ margin: 0, color: '#262626' }}>
            西舟菜园管理端
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            请登录您的管理员账户
          </Text>
          {import.meta.env.DEV && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                开发环境默认（prisma seed）：superadmin / CloudWest@5444
              </Text>
            </div>
          )}
        </div>

        <Form
          name="login"
          onFinish={handleLogin}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名或手机号' },
              {
                validator: (_, value) => {
                  const s = String(value || '').trim()
                  if (!s) return Promise.reject(new Error('请输入用户名或手机号'))
                  if (CN_MOBILE.test(s)) return Promise.resolve()
                  if (s.length >= 3) return Promise.resolve()
                  return Promise.reject(
                    new Error('请输入至少3个字符的用户名，或11位中国大陆手机号'),
                  )
                },
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名或手机号"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码长度不能少于6个字符' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: '48px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                fontSize: '16px',
                fontWeight: 500,
              }}
            >
              {loading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ©️ 2026 北京云起西舟科技有限公司 版权所有
            </Text>
          </div>
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <img
                src="/psb.png"
                alt="公安备案"
                style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 4 }}
              />
              <a
                href="https://beian.mps.gov.cn/#/query/webSearch?code=43010502002158"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                湘公网安备43010502002158号
              </a>
            </Text>
          </div>
          {icpNumber && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <a
                  href="https://beian.miit.gov.cn/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                {icpNumber}
                </a>
              </Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

export default LoginPage



